import React, { Suspense, forwardRef, lazy } from 'react';
import type { RichTextEditorProps } from '@mantine/rte';
import type { ReactQuillProps } from 'react-quill';

/*
 * The Quill editor stack (@mantine/rte, react-quill, quill, quill-mention) is
 * ~250 KB of JavaScript. Loading it on demand keeps it out of the main bundle
 * so the Lounge renders sooner; the editor chunk arrives right after first
 * paint. Refs pass through, so callers can still use ref.current.getEditor().
 */
const loadMantineRte = () =>
  import(/* webpackChunkName: "rich-text-editor" */ '@mantine/rte');
const loadReactQuill = () =>
  import(/* webpackChunkName: "rich-text-editor" */ 'react-quill');

const MantineRichTextEditor = lazy(() =>
  loadMantineRte().then((mod) => ({ default: mod.RichTextEditor }))
);
const QuillEditor = lazy(loadReactQuill);

/**
 * Warm the editor chunk once the page is idle, so tapping a comment box
 * never waits on the download. Safe to call more than once.
 */
export const preloadEditors = () => {
  const load = () => {
    loadMantineRte().catch(() => {});
    loadReactQuill().catch(() => {});
  };
  const w = window as any;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(load, { timeout: 4000 });
  } else {
    window.setTimeout(load, 2000);
  }
};

const EditorPlaceholder = ({ placeholder }: { placeholder?: React.ReactNode }) => (
  <div className='mw-editor-loading' aria-hidden='true'>
    {placeholder}
  </div>
);

export const RichTextEditor = forwardRef<any, RichTextEditorProps>((props, ref) => (
  <Suspense fallback={<EditorPlaceholder placeholder={props.placeholder} />}>
    <MantineRichTextEditor {...props} ref={ref} />
  </Suspense>
));
RichTextEditor.displayName = 'LazyRichTextEditor';

export const ReactQuill = forwardRef<any, ReactQuillProps>((props, ref) => (
  <Suspense fallback={<EditorPlaceholder placeholder={props.placeholder} />}>
    <QuillEditor {...props} ref={ref} />
  </Suspense>
));
ReactQuill.displayName = 'LazyReactQuill';

export default RichTextEditor;
